import { Estimate, EstimateStatus } from '@talon/web/lib/api';
import { useEstimateStore } from '@talon/web/src/context';
import { recalcKey } from '@talon/web/src/context/stores/estimateStore';
import { USD } from '@talon/web/src/utils/currency';
import { Box, Button, Card, Text } from 'grommet';
import { Refresh, ShareOption } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { env } from '../../../config/env';
import { ConfirmModal } from '../ConfirmModal';
import { EditableText } from '../EditableText';
import { SaveStatus, SaveStatusIndicator } from '../SaveStatusIndicator';
import { HeaderTotal } from '../PageHeader';
import { SegmentedControl } from '../SegmentedControl';

export interface IEstimateHeaderProps {
  estimate: Estimate;
  updateEstimate: (estimate: Estimate) => void;
}

export const EstimateHeader = observer(() => {
  /** Context **/
  const estimateStore = useEstimateStore();
  const location = useLocation();

  /** State **/
  const [showRerunModal, setShowRerunModal] = useState(false);

  /** Computed **/
  const saveStatus = estimateStore.isSaving ? SaveStatus.Saving : SaveStatus.Saved;
  const totalsBusy = estimateStore.isRecalculating(recalcKey.estimate);

  /** Methods **/

  const updateEstimate = async (updatedEstimate: Estimate) => {
    await estimateStore.updateEstimate(updatedEstimate);
    const estimate = await estimateStore.getEstimate(updatedEstimate.id, 'session_estimates,usage_estimates');
    if (estimate) estimateStore.setCurrentEstimate(estimate);
  };

  const copyShareLink = async () => {
    const link = new URL(location.pathname, env.clientUrl).href;
    await navigator.clipboard.writeText(link);
    toast.success('Copied link to clipboard.');
  };

  const recalculateEstimate = async () => {
    if (!estimateStore.currentEstimate) return;
    await estimateStore.recalculateEstimate(estimateStore.currentEstimate);
    setShowRerunModal(false);
  };

  /** Render **/
  const estimate = estimateStore.currentEstimate;
  const fringe = estimate ? estimate.total_plus_fringe - estimate.total : undefined;

  return (
    <Card pad="medium" background="white" margin="medium" gap="medium" overflow="auto">
      {/* Title and save status; Draft or Final */}
      <Box direction="row" wrap justify="between" align="start" gap="medium">
        <Box gap="xxsmall">
          <EditableText
            heading
            textWeight={600}
            textColor="text-heading"
            val={estimate?.title || ''}
            onChange={(t) => estimate?.id && updateEstimate({ ...estimate, title: t })}
            textSize="22px"
          />
          <SaveStatusIndicator saveStatus={saveStatus} boxProps={{ margin: 'none' }} />
        </Box>
        <Box align="end" gap="xxsmall">
          <SegmentedControl
            a11yTitle="Estimate status"
            options={[
              { label: 'Draft', value: EstimateStatus.Draft },
              { label: 'Final', value: EstimateStatus.Final },
            ]}
            value={estimate?.status ?? EstimateStatus.Draft}
            onChange={(status) => estimate?.id && updateEstimate({ ...estimate, status })}
          />
          <Text size="xsmall" color="text-meta">
            Final = approved by client
          </Text>
        </Box>
      </Box>

      {/* Totals, and what can be done with them */}
      <Box
        direction="row"
        wrap
        justify="between"
        align="end"
        gap="medium"
        pad={{ top: 'medium' }}
        border={{ side: 'top', color: 'border-divider' }}
      >
        <Box direction="row" gap="large" align="end" style={{ fontVariantNumeric: 'tabular-nums' }}>
          <HeaderTotal label="Total wages" value={USD(estimate?.total)} busy={totalsBusy} />
          <HeaderTotal label="Fringe" value={USD(fringe)} busy={totalsBusy} />
          <HeaderTotal label="Grand total" value={USD(estimate?.total_plus_fringe)} busy={totalsBusy} grand />
        </Box>
        <Box direction="row" gap="small">
          <Button secondary size="small" icon={<ShareOption size="16px" />} label="Share" onClick={copyShareLink} />
          <Button
            secondary
            size="small"
            icon={<Refresh size="16px" />}
            label="Recalculate"
            onClick={() => setShowRerunModal(true)}
          />
        </Box>
      </Box>

      {showRerunModal && (
        <ConfirmModal
          title="Recalculate this estimate?"
          content="We'll rebuild it from the current sessions and usage. Rates and fringe you edited by hand will be replaced."
          confirmLabel="Recalculate"
          onConfirm={() => recalculateEstimate()}
          onCancel={() => setShowRerunModal(false)}
        />
      )}
    </Card>
  );
});
